/**
 * DataTable — Global reusable table component with built-in sorting and pagination.
 *
 * Use this for EVERY table in the app. Features:
 * - Sortable columns (click header to sort asc/desc/none)
 * - Pagination with page size selector
 * - Row click handler
 * - Empty state
 * - Loading state
 * - Column-level render functions for custom content (badges, actions, etc.)
 *
 * Usage:
 *   <DataTable
 *     columns={[
 *       { key: 'name', label: 'Name', sortable: true },
 *       { key: 'status', label: 'Status', sortable: true, render: (row) => <Badge>{row.status}</Badge> },
 *       { key: 'actions', label: 'Actions', align: 'right', render: (row) => <Button>Edit</Button> },
 *     ]}
 *     data={items}
 *     onRowClick={(row) => navigate(`/items/${row.id}`)}
 *     pageSize={20}
 *     emptyMessage="No items found"
 *   />
 */
import { useState, useMemo, useCallback } from 'react';
import { ChevronUp, ChevronDown, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface DataTableColumn<T> {
  /** Unique key — used for sorting. Can be a dot path like 'user.name' */
  key: string;
  /** Display label in the header */
  label: string;
  /** Is this column sortable? Default: true */
  sortable?: boolean;
  /** Text alignment. Default: 'left' */
  align?: 'left' | 'center' | 'right';
  /** Width class (e.g., 'w-[40px]', 'w-[300px]') */
  width?: string;
  /** Custom render function. If not set, displays row[key] as string */
  render?: (row: T, index: number) => React.ReactNode;
  /** Header class override */
  headerClassName?: string;
  /** Cell class override */
  cellClassName?: string;
  /** Don't stop propagation on this cell (default: false). Set true for action columns. */
  stopPropagation?: boolean;
}

export interface DataTableProps<T> {
  /** Column definitions */
  columns: DataTableColumn<T>[];
  /** Data array */
  data: T[];
  /** Loading state — shows spinner */
  loading?: boolean;
  /** Default page size. Default: 20 */
  pageSize?: number;
  /** Show pagination controls. Default: true if data.length > pageSize */
  paginated?: boolean;
  /** Page size options */
  pageSizeOptions?: number[];
  /** Row click handler */
  onRowClick?: (row: T) => void;
  /** Empty state message */
  emptyMessage?: string;
  /** Row height class. Default: 'h-[56px]' */
  rowHeight?: string;
  /** Row key function. Default: (row) => row.id */
  rowKey?: (row: T) => string;
  /** Default sort column key */
  defaultSortKey?: string;
  /** Default sort direction */
  defaultSortDirection?: 'asc' | 'desc';
  /** Minimum table width for horizontal scroll */
  minWidth?: string;
  /** Additional className for the table container Card */
  className?: string;
  /** Prefix content before table (e.g., bulk action bar) */
  headerContent?: React.ReactNode;
}

interface SortConfig {
  key: string;
  direction: 'asc' | 'desc' | null;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getNestedValue(obj: any, path: string): any {
  return path.split('.').reduce((acc, key) => acc?.[key], obj);
}

function compareValues(a: any, b: any, direction: 'asc' | 'desc'): number {
  if (a == null && b == null) return 0;
  if (a == null) return direction === 'asc' ? 1 : -1;
  if (b == null) return direction === 'asc' ? -1 : 1;

  if (typeof a === 'string' && typeof b === 'string') {
    return direction === 'asc' ? a.localeCompare(b) : b.localeCompare(a);
  }
  if (typeof a === 'number' && typeof b === 'number') {
    return direction === 'asc' ? a - b : b - a;
  }
  if (a instanceof Date && b instanceof Date) {
    return direction === 'asc' ? a.getTime() - b.getTime() : b.getTime() - a.getTime();
  }
  // Fallback: convert to string
  const sa = String(a);
  const sb = String(b);
  return direction === 'asc' ? sa.localeCompare(sb) : sb.localeCompare(sa);
}

// ─── SortableHeader (inline) ─────────────────────────────────────────────────

function SortableHeaderCell({
  label, sortKey, currentSort, onSort, className,
}: {
  label: string; sortKey: string; currentSort: SortConfig; onSort: (key: string) => void; className?: string;
}) {
  const isSorted = currentSort.key === sortKey;
  const direction = isSorted ? currentSort.direction : null;

  // C-A11Y cluster (issue #767 AGENTS-A11Y-001 / AGENTS-HUB-SORT):
  // Render the sort control as a semantic <button> rather than a click-only
  // <div>. Adds keyboard support (Enter/Space via native button behaviour),
  // a descriptive aria-label, and a focus-visible ring so keyboard + AT users
  // get the same affordance as mouse users. Do not put aria-sort on the
  // button: axe correctly flags it because aria-sort belongs on a columnheader
  // or rowheader, not on an interactive control.
  const sortStateLabel = isSorted
    ? (direction === 'asc' ? ', sorted ascending' : direction === 'desc' ? ', sorted descending' : '')
    : '';

  return (
    <button
      type="button"
      role="button"
      aria-label={`Sort by ${label}${sortStateLabel}`}
      className={cn(
        'flex items-center gap-1.5 group select-none bg-transparent border-0 p-0 m-0 text-left font-inherit',
        'cursor-pointer hover:opacity-90',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 rounded-sm',
        className,
      )}
      onClick={() => onSort(sortKey)}
    >
      <span className={cn('transition-colors', isSorted ? 'text-foreground font-bold' : 'text-muted-foreground group-hover:text-foreground')}>
        {label}
      </span>
      <span className="flex flex-col" aria-hidden="true">
        {(!isSorted || direction === 'asc') && (
          <ChevronUp className={cn('h-3 w-3 transition-colors -mb-1', isSorted && direction === 'asc' ? 'text-primary stroke-[3px]' : 'text-muted-foreground/30')} />
        )}
        {(!isSorted || direction === 'desc') && (
          <ChevronDown className={cn('h-3 w-3 transition-colors -mt-1', isSorted && direction === 'desc' ? 'text-primary stroke-[3px]' : 'text-muted-foreground/30')} />
        )}
      </span>
    </button>
  );
}

// ─── DataTable Component ─────────────────────────────────────────────────────

export function DataTable<T extends Record<string, any>>({
  columns,
  data,
  loading = false,
  pageSize: initialPageSize = 20,
  paginated = true,
  pageSizeOptions = [10, 20, 50, 100],
  onRowClick,
  emptyMessage = 'No data found',
  rowHeight = 'h-[56px]',
  rowKey = (row) => row.id ?? String(Math.random()),
  defaultSortKey,
  defaultSortDirection = 'asc',
  minWidth,
  className,
  headerContent,
}: DataTableProps<T>) {
  // Sort state
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    key: defaultSortKey ?? '',
    direction: defaultSortKey ? defaultSortDirection : null,
  });

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialPageSize);

  // Reset page when data changes
  const dataLength = data.length;
  useMemo(() => { setCurrentPage(1); }, [dataLength]);

  // Sort handler — misc-cluster-aaa #582 (AGENTS-HUB-018/019):
  // Pre-fix this cycled asc → desc → null → asc. The third click
  // landed on a no-sort state and reverted to the API's natural
  // order, which on /agents also happens to start with
  // `Argus Architect`; reporters observed "click does nothing" even
  // though each click did move state. Post-fix the cycle is a
  // 2-state asc ↔ desc toggle so every click visibly flips order.
  const handleSort = useCallback((key: string) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
    setCurrentPage(1);
  }, []);

  // Sort + paginate data
  const processedData = useMemo(() => {
    let result = [...data];

    // Sort
    if (sortConfig.key && sortConfig.direction) {
      result.sort((a, b) => {
        const aVal = getNestedValue(a, sortConfig.key);
        const bVal = getNestedValue(b, sortConfig.key);
        return compareValues(aVal, bVal, sortConfig.direction!);
      });
    }

    return result;
  }, [data, sortConfig]);

  const totalPages = Math.ceil(processedData.length / pageSize);
  const showPagination = paginated && processedData.length > pageSize;

  const paginatedData = useMemo(() => {
    if (!paginated) return processedData;
    const start = (currentPage - 1) * pageSize;
    return processedData.slice(start, start + pageSize);
  }, [processedData, currentPage, pageSize, paginated]);

  return (
    <div className={cn('space-y-3', className)}>
      {headerContent}

      <div className="rounded-lg border bg-card shadow-sm overflow-hidden">
        <div className={cn('overflow-x-auto', minWidth && `min-w-0`)}>
          {loading ? (
            <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
          ) : (
            <Table className={minWidth ? `min-w-[${minWidth}]` : undefined} style={minWidth ? { minWidth } : undefined}>
              <TableHeader className="bg-muted/30">
                <TableRow className="h-10">
                  {columns.map(col => (
                    <TableHead
                      key={col.key}
                      className={cn(col.width, col.headerClassName, col.align === 'right' && 'text-right', col.align === 'center' && 'text-center')}
                    >
                      {col.sortable !== false ? (
                        <SortableHeaderCell
                          label={col.label}
                          sortKey={col.key}
                          currentSort={sortConfig}
                          onSort={handleSort}
                        />
                      ) : (
                        <span className="text-muted-foreground">{col.label}</span>
                      )}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedData.length > 0 ? paginatedData.map((row, rowIdx) => (
                  <TableRow
                    key={rowKey(row)}
                    className={cn(
                      rowHeight,
                      'hover:bg-muted/10 transition-colors',
                      onRowClick && 'cursor-pointer'
                    )}
                    onClick={() => onRowClick?.(row)}
                  >
                    {columns.map(col => (
                      <TableCell
                        key={col.key}
                        className={cn(col.cellClassName, col.align === 'right' && 'text-right', col.align === 'center' && 'text-center')}
                        onClick={col.stopPropagation ? (e) => e.stopPropagation() : undefined}
                      >
                        {col.render ? col.render(row, rowIdx) : String(getNestedValue(row, col.key) ?? '')}
                      </TableCell>
                    ))}
                  </TableRow>
                )) : (
                  <TableRow>
                    <TableCell colSpan={columns.length} className="h-32 text-center text-muted-foreground">
                      {emptyMessage}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </div>
      </div>

      {/* Footer — always visible: shows total count, page info, page size selector, prev/next */}
      {paginated && processedData.length > 0 && (
        <div className="flex items-center justify-between text-sm text-muted-foreground px-1">
          <div className="flex items-center gap-2">
            <span>Showing {((currentPage - 1) * pageSize) + 1}–{Math.min(currentPage * pageSize, processedData.length)} of {processedData.length} total</span>
            <span className="text-muted-foreground/50">|</span>
            <Select value={String(pageSize)} onValueChange={(v) => { setPageSize(Number(v)); setCurrentPage(1); }}>
              <SelectTrigger className="h-7 w-[70px] text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                {pageSizeOptions.map(s => <SelectItem key={s} value={String(s)} className="text-xs">{s}</SelectItem>)}
              </SelectContent>
            </Select>
            <span className="text-xs">per page</span>
          </div>
          <div className="flex items-center gap-1">
            <Button variant="outline" size="sm" className="h-7 text-xs" disabled={currentPage <= 1} onClick={() => setCurrentPage(p => p - 1)}>
              <ChevronLeft className="h-3.5 w-3.5 mr-1" />Previous
            </Button>
            <span className="px-2 text-xs">Page {currentPage} of {totalPages || 1}</span>
            <Button variant="outline" size="sm" className="h-7 text-xs" disabled={currentPage >= totalPages} onClick={() => setCurrentPage(p => p + 1)}>
              Next<ChevronRight className="h-3.5 w-3.5 ml-1" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// Re-export SortableHeader for cases where DataTable isn't used but sorting is needed
export { SortableHeaderCell as SortableHeader };
export type { SortConfig };
export { getNestedValue, compareValues };

// ─── Hook for grid-based tables ─────────────────────────────────────────────
// Provides sort state + sorted data for CSS-grid tables that don't use <DataTable>.
//
// Usage:
//   const { sorted, sortConfig, handleSort } = useTableSort(data, 'name', 'asc');
//   // In header: <SortableHeader label="Name" sortKey="name" currentSort={sortConfig} onSort={handleSort} />
//   // In body:   sorted.map(row => ...)

export function useTableSort<T>(
  data: T[],
  defaultKey?: string,
  defaultDirection: 'asc' | 'desc' = 'asc',
) {
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    key: defaultKey ?? '',
    direction: defaultKey ? defaultDirection : null,
  });

  // misc-cluster-aaa #582 (AGENTS-HUB-018/019): mirror the 2-state
  // toggle from the DataTable handler above. The pre-fix asc → desc →
  // null cycle landed on a "no sort" state on the third click and
  // visually reverted to the API's natural order on /agents and every
  // other table that calls useTableSort.
  const handleSort = useCallback((key: string) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  }, []);

  const sorted = useMemo(() => {
    const result = [...data];
    if (sortConfig.key && sortConfig.direction) {
      result.sort((a, b) => {
        const aVal = getNestedValue(a, sortConfig.key);
        const bVal = getNestedValue(b, sortConfig.key);
        return compareValues(aVal, bVal, sortConfig.direction!);
      });
    }
    return result;
  }, [data, sortConfig]);

  return { sorted, sortConfig, handleSort };
}
